import React, { useState } from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const visualFeatures = [
  {
    title: 'Screen OCR',
    description: "Capture text from anywhere on your screen instantly using macOS's built-in Vision framework.",
    image: { src: '/feature1.png', alt: 'Ghost Text screen OCR capturing on-screen text on macOS' },
  },
  {
    title: 'Menu bar access',
    description: 'Always ready from your menu bar with one click or the global shortcut ⌘⇧2.',
    image: { src: '/feature2.png', alt: 'Ghost Text in the macOS menu bar with quick capture actions' },
  },
  {
    title: 'Smart clipboard',
    description: 'Recognized text is copied automatically, with a history of everything you have captured.',
    image: { src: '/feature3.png', alt: 'Ghost Text smart clipboard settings and capture history' },
  },
  {
    title: 'Drag & drop OCR',
    description: 'Drop images, screenshots, or files straight onto the app to extract their text.',
    image: { src: '/feature4.png', alt: 'Text extracted by Ghost Text from an image dropped onto the app' },
  },
];

const utilityFeatures = [
  {
    title: '100% offline',
    description: 'All OCR happens locally on your Mac. Your screen never leaves your device.',
  },
  {
    title: 'Lightweight & fast',
    description: 'Runs silently in the background without slowing your Mac down.',
  },
  {
    title: 'Browser search',
    description: 'Search any captured text in your default browser with one click.',
  },
  {
    title: 'Custom sounds',
    description: 'Pick the sound that plays when a capture succeeds — or none at all.',
  },
];

const Features = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const headerRef = useScrollAnimation();
  const utilityRef = useScrollAnimation();
  const visualRef = useScrollAnimation();

  return (
    <section id="features" className="relative py-20 lg:py-28">
      {/* Spectral glow */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/3 right-[-16rem] w-[36rem] h-[36rem] bg-spectral/10 rounded-full blur-[140px]"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div ref={headerRef} className="text-center mb-16 animate-slide-up">
          <span className="eyebrow">Features</span>
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-bone mt-4 mb-5">
            Built for text that hides
          </h2>
          <p className="text-lg lg:text-xl text-mist max-w-2xl mx-auto">
            Screenshots, videos, locked PDFs, error dialogs, remote desktops —
            everything on screen becomes selectable.
          </p>
        </div>

        {/* Utility cards */}
        <div ref={utilityRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 mb-5 animate-slide-up">
          {utilityFeatures.map((feature) => (
            <div key={feature.title} className="card-capture p-6">
              <h3 className="font-display text-lg font-semibold text-bone mb-2">
                {feature.title}
              </h3>
              <p className="text-mist text-sm leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Visual feature cards */}
        <div ref={visualRef} className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5 animate-slide-up">
          {visualFeatures.map((feature) => (
            <div key={feature.title} className="card-capture p-6 lg:p-8 flex flex-col">
              <h3 className="font-display text-xl lg:text-2xl font-semibold text-bone mb-2">
                {feature.title}
              </h3>
              <p className="text-mist leading-relaxed mb-6 max-w-md">
                {feature.description}
              </p>
              <button
                className="block w-full mt-auto rounded-xl overflow-hidden border border-phantom/10 hover:border-phantom/40 transition-colors duration-300 cursor-zoom-in"
                onClick={() => setSelectedImage(feature.image)}
                aria-label={`Enlarge screenshot: ${feature.image.alt}`}
              >
                <img
                  src={feature.image.src}
                  alt={feature.image.alt}
                  loading="lazy"
                  className="w-full h-64 lg:h-80 object-cover object-top block"
                />
              </button>
            </div>
          ))}
        </div>

        {/* Image Modal */}
        {selectedImage && (
          <div
            className="fixed inset-0 bg-void/90 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedImage(null)}
          >
            <div className="relative max-w-4xl max-h-full">
              <button
                className="absolute -top-10 right-0 font-mono text-sm text-mist hover:text-bone z-10"
                onClick={() => setSelectedImage(null)}
              >
                esc / close ✕
              </button>
              <img
                src={selectedImage.src}
                alt={selectedImage.alt}
                className="max-w-full max-h-[85vh] object-contain rounded-xl border border-phantom/20 shadow-mac-lg"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Features;
